import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import cmsService from 'src/services/cms-service';
import { CMSSpecies, Resources, TaxonomySummary } from 'src/shared/types';
import SpeciesTags from 'src/app/maps/views/species-map/components/species-map-sidebar/components/species-tags/SpeciesTags';
import TestTypeChip from 'src/shared/components/test-type-chip/TestTypeChip';
import UpgradeSubscriptionButton from 'src/shared/components/upgrade-subscription-button/UpgradeSubscriptionButton';
import useSpeciesMapAnalytics from 'src/app/maps/views/species-map/hooks/useSpeciesMapAnalytics';
import { getConservationStatusTag } from 'src/shared/helpers/iucnStatusHelper';
import { useSpeciesTestTypesQuery } from 'src/shared/api/species/speciesGraphSlice';
import useProject from 'src/shared/hooks/useProject';
import FrogHex from 'src/assets/images/general/frog-hex.png';
import useFeature from 'src/shared/hooks/useFeature';
import styles from './SpeciesInfoPopup.module.scss';

export type SpeciesInfoPopupProps = {
    scientificName: string;
    bottomActions?: React.ReactNode;
    taxonomySummary: TaxonomySummary;
};

export const SpeciesInfoPopup = (props: SpeciesInfoPopupProps) => {
    const { scientificName, taxonomySummary, bottomActions = null } = props;
    const [speciesDetails, setSpeciesDetails] = useState<CMSSpecies | null>(null);
    const { currentProjectId } = useProject();
    const speciesData = taxonomySummary.find(entry => entry.taxonomy.tax_species === scientificName) || null;
    const { currentData: speciesTestTypesData } = useSpeciesTestTypesQuery(
        { projectId: currentProjectId || '' },
        { skip: !currentProjectId }
    );

    const { hasResource } = useFeature();

    const { trackUpgradeSubscriptionClick } = useSpeciesMapAnalytics();

    useEffect(() => {
        const fetchSpeciesDetails = async () => {
            const details = await cmsService.getSpeciesByScientificName(scientificName);
            // Check if the image can be loaded
            if (!details?.image?.medium) {
                setSpeciesDetails(details);
                return;
            }

            const img = new Image();
            img.src = details.image.medium;

            img.onload = () => setSpeciesDetails(details);

            img.onerror = () => {
                details.image.medium = '';
                setSpeciesDetails(details);
            };
        };

        if (scientificName && !speciesDetails) {
            fetchSpeciesDetails();
        }
    }, [scientificName, speciesDetails]);

    if (!speciesData) {
        return null;
    }

    const commonName = speciesData.taxonomy.common_name;

    const { iucnStatusLabel, iucnStatusType, chegdGroup, isInvasive } = getConservationStatusTag(speciesData.conservationStatus);
    const testTypes = speciesTestTypesData?.fetchSpeciesTestTypes?.find(testType => testType.species === scientificName)?.testTypes || [];
    const hasSpeciesMapFeature = hasResource({
        resource: Resources.SPECIES_INSIGHTS_MAP,
    });

    const areTagsVisible = [isInvasive, iucnStatusLabel, chegdGroup].some(Boolean);

    const descriptionClassNames = classNames(styles.description, {
        ['overflow-hidden h-[80px] select-none']: !hasSpeciesMapFeature,
    });

    return (
        <div
            className='w-[520px] min-h-fit rounded-lg overflow-hidden shadow-md p-6 bg-white flex flex-col gap-5'
            data-testid='species-info-popup'
        >
            <div className='flex flex-row gap-5'>
                {speciesDetails?.image?.medium ? (
                    <img
                        src={speciesDetails?.image?.medium}
                        alt={commonName || scientificName}
                        className='w-[202.5px] h-[113px] rounded-lg'
                    ></img>
                ) : (
                    <div className='bg-grey-0 rounded-lg h-[113px] w-[202.5px] flex items-center justify-center flex-col'>
                        <img src={FrogHex} alt={commonName || scientificName}></img>
                        <div className='text-sm text-grey-60'>No image available</div>
                    </div>
                )}

                <div className='flex flex-col gap-1'>
                    {commonName && <div className='text-lg text-primary'>{commonName}</div>}
                    {scientificName && <div className='text-sm text-black italic'>{scientificName}</div>}
                    {areTagsVisible && (
                        <div className={styles.iucnStatus}>
                            <SpeciesTags
                                iucnStatusType={iucnStatusType}
                                isInvasive={isInvasive}
                                iucnStatusLabel={iucnStatusLabel}
                                chegdGroup={chegdGroup}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.popupContent}>
                {speciesDetails?.description && (
                    <>
                        <div className='relative'>
                            <div className={descriptionClassNames} dangerouslySetInnerHTML={{ __html: speciesDetails?.description }}></div>
                            {
                                /* Below div is to display blur overlay on description for freemium subscription */
                                !hasSpeciesMapFeature && (
                                    <div className='absolute w-full h-3 bg-gradient-to-b from-white/0 via-white/90 to-white bottom-0'>
                                        &nbsp;
                                    </div>
                                )
                            }
                        </div>
                        {!hasSpeciesMapFeature && (
                            <div className='flex flex-col gap-3 p-4 bg-grey-0 w-full rounded-lg'>
                                <div className='text-md'>Unlock full species descriptions with Species Insights</div>
                                <div className='text-xs'>
                                    Discover the full story of your site&apos;s biodiversity health. Our subscriptions give you powerful,
                                    data-driven insights into the species and ecosystems that matter most — powered by cutting-edge eDNA
                                    technology.
                                </div>
                                <UpgradeSubscriptionButton
                                    buttonClassName='h-[30px] p-2 text-xs'
                                    onClick={() => trackUpgradeSubscriptionClick({ section: 'species_info_popup' })}
                                />
                            </div>
                        )}
                    </>
                )}
                <div className={styles.testTypesWrapper}>
                    <div className='text-sm text-grey-80'>Data sources</div>
                    <div className={styles.testTypes}>
                        {testTypes.map(testType => {
                            return (
                                <TestTypeChip
                                    key={testType.habitatAssayKey}
                                    testTypeKey={testType.habitatAssayKey}
                                    fallbackContent={
                                        <>
                                            <div>{testType.habitatName}</div>
                                            <div>{testType.assayName}</div>
                                        </>
                                    }
                                />
                            );
                        })}
                    </div>
                </div>

                {bottomActions}
            </div>
        </div>
    );
};

export default SpeciesInfoPopup;
